import {
  FRUIT_POLLINATION_DATA,
  type FruitChillBand,
  type FruitPollinationFruitType,
  type FruitPollinationTypeDefinition,
  type FruitPollinationVariety,
  type FruitSelfFertility,
} from '@/data/fruitPollination';

export type PartnerRank = 'best' | 'good' | 'okay' | 'avoid';
export type PollinationMode = 'single' | 'compare';

export interface PollinationPartnerMatch {
  variety: FruitPollinationVariety;
  rank: PartnerRank;
  score: number;
  bloomFit: 'same' | 'nearby' | 'far';
  zoneFit: 'good' | 'outside' | 'unknown';
  chillFit: 'good' | 'mixed' | 'poor' | 'unknown';
  direction: 'supports-selected' | 'supported-by-selected';
  reason: string;
  warnings: string[];
}

export interface PollinationPairCheck {
  status: 'good' | 'limited' | 'not-compatible';
  summary: string;
  details: string[];
}

export interface PlantingGuidance {
  minimumSetup: string;
  idealSetup: string;
  fallbackPlan: string;
}

export interface FruitPollinationResult {
  fruitType: FruitPollinationTypeDefinition;
  selectedVariety: FruitPollinationVariety;
  secondVariety: FruitPollinationVariety | null;
  zone: number | null;
  selfFertilityLabel: string;
  /** Sentence form of the label above, for prose and for the explanation prompt. */
  selfFertilitySentence: string;
  crossPollinationRecommended: boolean;
  crossPollinationReason: string;
  bloomSummary: string;
  zoneSummary: string | null;
  importantWarnings: string[];
  bestMatches: PollinationPartnerMatch[];
  goodBackups: PollinationPartnerMatch[];
  lessIdealMatches: PollinationPartnerMatch[];
  avoidMatches: PollinationPartnerMatch[];
  pairCheck: PollinationPairCheck | null;
  plainLanguageNotes: string[];
  plantingGuidance: PlantingGuidance;
}

export interface PollinationAdditionSuggestion {
  variety: FruitPollinationVariety;
  rank: 'best' | 'good' | 'okay';
  coverageCount: number;
  improvesVarieties: FruitPollinationVariety[];
  reason: string;
}

export interface StoreModePairing {
  firstVariety: FruitPollinationVariety;
  secondVariety: FruitPollinationVariety;
  rank: 'best' | 'good' | 'okay' | 'avoid';
  summary: string;
}

export interface ExistingVarietyPartnerSummary {
  existingVariety: FruitPollinationVariety;
  bestPartners: StoreModePairing[];
  weakPartners: StoreModePairing[];
  missingMessage: string | null;
}

export interface CompareVarietiesResult {
  fruitType: FruitPollinationTypeDefinition;
  selectedVarieties: FruitPollinationVariety[];
  existingVarieties: FruitPollinationVariety[];
  candidateVarieties: FruitPollinationVariety[];
  usesExistingVarietyFocus: boolean;
  zone: number | null;
  verdict: string;
  bestPairing: StoreModePairing | null;
  otherWorkablePairings: StoreModePairing[];
  existingVarietySummaries: ExistingVarietyPartnerSummary[];
  generalAlternativePairings: StoreModePairing[];
  weakCombinations: StoreModePairing[];
  missingCoverage: string[];
  simpleRecommendation: string;
  bestAdditions: PollinationAdditionSuggestion[];
  importantWarnings: string[];
  plantingGuidance: PlantingGuidance;
  frostRiskNote: string | null;
}

const SELF_FERTILITY_LABELS: Record<FruitSelfFertility, string> = {
  'not-self-fertile': 'Needs another compatible variety',
  'partially-self-fertile': 'Can set some fruit alone, but does better with a partner',
  'self-fertile': 'Can fruit alone, but still benefits from a partner',
};

/**
 * The labels above are badge fragments. Reading them back as a sentence
 * predicate produced "Honeycrisp is needs another compatible variety", so prose
 * surfaces take these instead and leave the pills alone.
 */
const SELF_FERTILITY_SENTENCES: Record<FruitSelfFertility, (name: string) => string> = {
  'not-self-fertile': name => `${name} needs another compatible variety nearby to set fruit.`,
  'partially-self-fertile': name => `${name} can set some fruit alone, but it does better with a partner.`,
  'self-fertile': name => `${name} can fruit alone, but it still benefits from a partner.`,
};

const CHILL_BAND_ORDER: Record<FruitChillBand, number> = {
  'very-low': 0,
  low: 1,
  medium: 2,
  high: 3,
  'very-high': 4,
};

interface DirectionalCheck {
  works: boolean;
  warnings: string[];
}

interface CompatibilityEvaluation {
  donorToTarget: DirectionalCheck;
  targetToDonor: DirectionalCheck;
  rank: PartnerRank;
  score: number;
  bloomFit: PollinationPartnerMatch['bloomFit'];
  zoneFit: PollinationPartnerMatch['zoneFit'];
  chillFit: PollinationPartnerMatch['chillFit'];
  reason: string;
  warnings: string[];
}

interface RankedPairing extends StoreModePairing {
  score: number;
}

const EARLY_FROST_PRONE_FRUITS = new Set<FruitPollinationFruitType>([
  'pear',
  'plum',
  'cherry',
  'peach',
  'apricot',
  'nectarine',
  'serviceberry',
  'blueberry',
]);

function compareBloomDistance(a: FruitPollinationVariety, b: FruitPollinationVariety): number {
  return Math.abs(a.bloomOrder - b.bloomOrder);
}

function normalizeComparableText(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function varietyLookupCandidates(variety: FruitPollinationVariety): string[] {
  const rawParts = new Set<string>([
    variety.name,
    variety.id.replace(/-/g, ' '),
    variety.id,
    variety.name.replace(/[()]/g, ' '),
    ...(variety.aliases ?? []),
  ]);

  for (const segment of variety.name.split(/[\/(),]/)) {
    const trimmed = segment.trim();
    if (trimmed) rawParts.add(trimmed);
  }

  return [...rawParts]
    .map(part => normalizeComparableText(part))
    .filter(Boolean);
}

function bloomFitLabel(a: FruitPollinationVariety, b: FruitPollinationVariety): PollinationPartnerMatch['bloomFit'] {
  const distance = compareBloomDistance(a, b);
  if (distance === 0) return 'same';
  if (distance <= 1) return 'nearby';
  return 'far';
}

function zoneFit(variety: FruitPollinationVariety, zone: number | null): PollinationPartnerMatch['zoneFit'] {
  if (!zone || !variety.zoneRange) return 'unknown';
  if (zone >= variety.zoneRange.min && zone <= variety.zoneRange.max) return 'good';
  return 'outside';
}

function chillFit(a: FruitPollinationVariety, b: FruitPollinationVariety): PollinationPartnerMatch['chillFit'] {
  if (!a.chillBand || !b.chillBand) return 'unknown';
  const distance = Math.abs(CHILL_BAND_ORDER[a.chillBand] - CHILL_BAND_ORDER[b.chillBand]);
  if (distance === 0) return 'good';
  if (distance === 1) return 'mixed';
  return 'poor';
}

function zoneSummary(variety: FruitPollinationVariety, zone: number | null): string | null {
  if (!variety.zoneRange) return null;
  const rangeLabel = `Usually grown in USDA zones ${variety.zoneRange.min}-${variety.zoneRange.max}.`;
  if (!zone) return rangeLabel;
  if (zone >= variety.zoneRange.min && zone <= variety.zoneRange.max) {
    return `Your saved zone ${zone} is within this variety's usual range. ${rangeLabel}`;
  }
  return `Your saved zone ${zone} sits outside this variety's usual range of USDA zones ${variety.zoneRange.min}-${variety.zoneRange.max}. Pollination may still work, but the plant itself may not be the best fit for your site.`;
}

function buildFrostRiskNote(
  fruitType: FruitPollinationFruitType,
  varieties: FruitPollinationVariety[],
): string | null {
  const earliestBloom = varieties.length > 0
    ? Math.min(...varieties.map(variety => variety.bloomOrder))
    : null;

  if (!EARLY_FROST_PRONE_FRUITS.has(fruitType) && earliestBloom !== null && earliestBloom > 2) {
    return null;
  }

  return 'Late spring frost can still reduce fruit set even when pollination partners are compatible, especially for early-blooming plantings.';
}

function plantingGuidance(fruitType: FruitPollinationTypeDefinition): PlantingGuidance {
  return {
    minimumSetup: fruitType.minimumSetup,
    idealSetup: fruitType.idealSetup,
    fallbackPlan: fruitType.fallbackPlan,
  };
}

function bloomSummary(variety: FruitPollinationVariety): string {
  if (variety.compatibilityGroup) {
    return `Compatibility group ${variety.compatibilityGroup} - ${variety.bloomLabel}.`;
  }
  return `Bloom group ${variety.bloomGroup} - ${variety.bloomLabel}.`;
}

function selectedVarietyLabel(variety: FruitPollinationVariety): string {
  return variety.name;
}

function isSameFruitFamily(a: FruitPollinationVariety, b: FruitPollinationVariety): boolean {
  return a.fruitType === b.fruitType;
}

function sameExplicitCompatibility(target: FruitPollinationVariety, donor: FruitPollinationVariety): boolean {
  return target.compatibleVarietyIds?.includes(donor.id) ?? false;
}

function explicitIncompatibility(a: FruitPollinationVariety, b: FruitPollinationVariety): boolean {
  return (a.incompatibleVarietyIds?.includes(b.id) ?? false)
    || (b.incompatibleVarietyIds?.includes(a.id) ?? false);
}

function canDonorPollinate(target: FruitPollinationVariety, donor: FruitPollinationVariety): DirectionalCheck {
  if (target.id === donor.id) {
    return { works: false, warnings: [] };
  }

  if (!isSameFruitFamily(target, donor)) {
    return { works: false, warnings: [] };
  }

  const warnings: string[] = [];

  if (explicitIncompatibility(target, donor)) {
    warnings.push('These two are listed as a poor match.');
    return { works: false, warnings };
  }

  if (donor.pollenSterile) {
    warnings.push(`${donor.name} is a poor pollen source.`);
    return { works: false, warnings };
  }

  if (donor.weakPollinator) {
    warnings.push(`${donor.name} is usually a weak pollinizer.`);
  }

  if (sameExplicitCompatibility(target, donor)) {
    return { works: true, warnings };
  }

  const bloomDistance = compareBloomDistance(target, donor);

  switch (target.fruitType) {
    case 'apple':
      return { works: bloomDistance <= 1, warnings };

    case 'pear':
      if (bloomDistance > 1) return { works: false, warnings };
      if (!target.speciesGroup || !donor.speciesGroup) return { works: true, warnings };
      return { works: target.speciesGroup === donor.speciesGroup || bloomDistance === 0, warnings };

    case 'plum':
      if (bloomDistance > 1) return { works: false, warnings };
      return { works: target.speciesGroup === donor.speciesGroup, warnings };

    case 'cherry':
      if (target.speciesGroup !== donor.speciesGroup) return { works: false, warnings };
      if (bloomDistance > 1) return { works: false, warnings };
      if (target.speciesGroup === 'tart') return { works: true, warnings };
      if (donor.universalDonor) return { works: true, warnings };
      if (!target.compatibilityGroup || !donor.compatibilityGroup) return { works: true, warnings };
      return { works: target.compatibilityGroup !== donor.compatibilityGroup, warnings };

    case 'blueberry':
      return {
        works: target.speciesGroup === donor.speciesGroup && bloomDistance <= 1,
        warnings,
      };

    case 'mango':
      return { works: bloomDistance <= 1, warnings };

    case 'peach':
    case 'apricot':
    case 'nectarine':
    case 'mulberry':
    case 'elderberry':
    case 'serviceberry':
    case 'quince':
    case 'gooseberry':
    case 'jujube':
    case 'honeyberry':
    case 'hazelnut':
    case 'cranberry':
      return { works: bloomDistance <= 1, warnings };

    case 'raspberry': {
      const raspberryCompatible = (a: string | undefined, b: string | undefined): boolean => {
        if (!a || !b) return false;
        if (a === b) return true;
        // Purple raspberries (red x black cross) are compatible with both red-yellow and black groups
        if (a === 'purple') return b === 'red-yellow' || b === 'black';
        if (b === 'purple') return a === 'red-yellow' || a === 'black';
        return false;
      };
      return {
        works: raspberryCompatible(target.speciesGroup, donor.speciesGroup) && bloomDistance <= 1,
        warnings,
      };
    }

    case 'blackberry':
    case 'strawberry':
    case 'aronia':
    case 'goji-berry':
    case 'lingonberry':
      return { works: true, warnings };

    case 'pawpaw':
      return { works: bloomDistance <= 1, warnings };

    case 'persimmon':
      return {
        works: target.speciesGroup === donor.speciesGroup && bloomDistance <= 1,
        warnings,
      };

    case 'fig':
      return { works: true, warnings };

    case 'grape':
    case 'pomegranate':
      return { works: bloomDistance <= 1, warnings };

    case 'passion-fruit':
      return {
        works: target.speciesGroup === donor.speciesGroup && bloomDistance <= 1,
        warnings,
      };

    case 'kiwi':
    case 'hardy-kiwi':
      if (target.pollinationRole === 'male-pollinizer') {
        return { works: false, warnings };
      }
      if (donor.pollinationRole === 'male-pollinizer') {
        return {
          works: target.speciesGroup === donor.speciesGroup && bloomDistance <= 1,
          warnings,
        };
      }
      if (target.id === 'issai' && donor.id === 'issai') {
        return { works: true, warnings };
      }
      return { works: false, warnings };

    case 'currant':
      return {
        works: target.speciesGroup === donor.speciesGroup && bloomDistance <= 1,
        warnings,
      };

    default:
      return { works: false, warnings };
  }
}

function rankFromScore(score: number): PartnerRank {
  if (score >= 85) return 'best';
  if (score >= 70) return 'good';
  if (score >= 55) return 'okay';
  return 'avoid';
}

function sentenceCaseJoin(parts: string[]): string {
  return parts.filter(Boolean).join(' ');
}

function evaluateCompatibility(
  target: FruitPollinationVariety,
  candidate: FruitPollinationVariety,
  zone: number | null,
  direction: 'supports-selected' | 'supported-by-selected' = 'supports-selected',
): CompatibilityEvaluation {
  const donor = direction === 'supports-selected' ? candidate : target;
  const receiver = direction === 'supports-selected' ? target : candidate;

  const donorToTarget = canDonorPollinate(receiver, donor);
  const targetToDonor = canDonorPollinate(donor, receiver);
  const bloomFit = bloomFitLabel(target, candidate);
  const zoneState = zoneFit(candidate, zone);
  const chillState = chillFit(target, candidate);

  let score = donorToTarget.works ? 50 : 0;
  if (sameExplicitCompatibility(receiver, donor)) score += 20;
  if (bloomFit === 'same') score += 18;
  else if (bloomFit === 'nearby') score += 10;

  if (targetToDonor.works) score += 8;

  if (zoneState === 'good') score += 10;
  else if (zoneState === 'unknown') score += 4;
  else score -= 10;

  if (chillState === 'good') score += 10;
  else if (chillState === 'mixed') score += 4;
  else if (chillState === 'poor') score -= 10;

  if (candidate.weakPollinator) score -= 12;

  const warnings = [...donorToTarget.warnings];

  if (!donorToTarget.works) {
    score = Math.min(score, 20);
    if (bloomFit === 'far') warnings.push('Bloom timing looks too far apart.');
    if (receiver.speciesGroup && donor.speciesGroup && receiver.speciesGroup !== donor.speciesGroup) {
      warnings.push('These are not in the same pollination group.');
    }
  }

  const bloomText = bloomFit === 'same'
    ? 'Blooms in the same window.'
    : bloomFit === 'nearby'
      ? 'Blooms close enough to overlap in many home plantings.'
      : 'Bloom timing is not a strong match.';

  const zoneText = zoneState === 'good'
    ? `Usually a good zone fit for zone ${zone}.`
    : zoneState === 'outside'
      ? `May not be the best climate fit for zone ${zone}.`
      : 'Zone fit is unknown or not a major tie-breaker here.';

  const chillText = chillState === 'good'
    ? 'Chill needs line up well.'
    : chillState === 'mixed'
      ? 'Chill needs are fairly close.'
      : chillState === 'poor'
        ? 'Chill needs may pull these apart in real gardens.'
        : '';

  const reason = sentenceCaseJoin([bloomText, zoneText, chillText]);

  return {
    donorToTarget,
    targetToDonor,
    rank: donorToTarget.works ? rankFromScore(score) : 'avoid',
    score,
    bloomFit,
    zoneFit: zoneState,
    chillFit: chillState,
    reason,
    warnings,
  };
}

function sameTypeWarning(selected: FruitPollinationVariety, secondVariety: FruitPollinationVariety | null): string[] {
  const warnings = new Set<string>();

  for (const warning of selected.warnings ?? []) warnings.add(warning);

  // Most triploids carry their own authored warning, which is more specific
  // than this one. Adding both said the same thing twice in a row.
  const hasAuthoredPollenWarning = (selected.warnings ?? []).some(warning =>
    /triploid|pollen/i.test(warning));

  if (selected.pollenSterile && !hasAuthoredPollenWarning) {
    warnings.add('Triploid or pollen-sterile warning: this variety may still fruit, but its pollen usually does not help another tree.');
  }

  if (selected.fruitType === 'plum') {
    warnings.add('Do not casually mix European, Japanese, and hardy hybrid plums. Plum group matters.');
  }

  if (selected.fruitType === 'blueberry') {
    warnings.add('Blueberry partners work best within the same blueberry type, such as northern highbush with northern highbush or rabbiteye with rabbiteye.');
  }

  if (selected.fruitType === 'cherry' && selected.speciesGroup === 'sweet') {
    warnings.add('Sweet cherries need carefully matched sweet cherry partners. Tart cherries are not a substitute.');
  }

  if (selected.fruitType === 'kiwi') {
    warnings.add('Most kiwis need at least one male pollinizer for nearby fruiting vines. Jenny is the easier exception, not the rule.');
  }

  if (selected.fruitType === 'hardy-kiwi') {
    warnings.add('Most hardy kiwis need at least one male pollinizer for nearby fruiting vines. Issai is the easier exception, not the rule.');
  }

  if (selected.fruitType === 'currant') {
    warnings.add('Red and white currants are best matched with each other. Black currants are best matched with black currants.');
  }

  if (selected.fruitType === 'persimmon') {
    warnings.add('American and Asian persimmons are best matched within their own group.');
  }

  if (selected.fruitType === 'honeyberry') {
    warnings.add('Honeyberries are not self-fertile. A second different variety with overlapping bloom is required for fruit set. Bumblebees are the primary pollinator - plant early-blooming companions like crocus or chives nearby to support them.');
  }

  if (selected.fruitType === 'hazelnut') {
    warnings.add('Hazelnut self-incompatibility is governed by S-alleles. Two varieties that share dominant S-alleles cannot cross-pollinate each other even when bloom timing overlaps. Known incompatible pairs are flagged in this tool - verify S-allele compatibility for any unlisted pairing.');
  }

  if (selected.fruitType === 'cranberry') {
    warnings.add('Cranberry sets less than 5% fruit from self-pollination. A second variety is strongly recommended - expect very little harvest from a solo planting. Bumblebees are far more effective than honeybees at pollinating cranberry flowers.');
  }

  if (selected.fruitType === 'raspberry' && selected.speciesGroup === 'black') {
    warnings.add('Black raspberries do not cross-pollinate effectively with red or yellow varieties. Pair black raspberry varieties with other blacks for best results.');
  }

  if (selected.fruitType === 'lingonberry') {
    warnings.add('Lingonberry yields meaningfully more and larger fruit with a designated pollinizer nearby. Interplant Red Pearl at roughly 1 plant per 5 to 10 plants of other varieties for the best results.');
  }

  if (selected.fruitType === 'mulberry') {
    warnings.add('Named fruiting mulberries are usually the safest picks. Seedling mulberries can be male and never fruit.');
  }

  if (selected.fruitType === 'fig') {
    warnings.add('Common figs do not need a second tree for pollination. Extra varieties are optional, not required.');
  }

  if (selected.fruitType === 'grape') {
    warnings.add('Most grapes are self-fertile. A second vine may improve yield a bit, but it is usually optional rather than required.');
  }

  if (selected.fruitType === 'pomegranate') {
    warnings.add('Most pomegranates are self-fertile. Extra plants can improve fruit set, but one plant can still crop on its own.');
  }

  if (selected.fruitType === 'passion-fruit') {
    warnings.add('Passion fruit partners work best within the same species group. Maypop is best matched with another maypop or other compatible Passiflora incarnata planting, not just any passion vine.');
  }

  if (selected.fruitType === 'mango') {
    warnings.add('Most named mangoes can fruit on their own, but a second mango nearby can still improve fruit set and crop consistency in warm climates.');
    warnings.add('Mangoes are only dependable outdoors in frost-free or near-frost-free climates. In cooler zones they usually need a container, greenhouse, or winter protection.');
  }

  if (
    selected.fruitType === 'pear'
    && selected.speciesGroup
    && secondVariety?.speciesGroup
    && selected.speciesGroup !== secondVariety.speciesGroup
  ) {
    warnings.add('Asian and European pears can overlap, but same-group pear partners are usually the safer bet.');
  }

  return [...warnings];
}

function buildNotes(selected: FruitPollinationVariety, fruitType: FruitPollinationTypeDefinition): string[] {
  const notes = new Set<string>();
  notes.add(fruitType.partnerDistanceNote);

  if (selected.chillHours) {
    notes.add(`Chill note: ${selected.chillHours}.`);
  }

  for (const note of selected.notes ?? []) notes.add(note);

  if (selected.pollenSterile) {
    notes.add('Triploid means this variety has pollen that usually does not work well for pollinating other trees.');
  }

  if (selected.pollinationRole === 'male-pollinizer') {
    notes.add('This is a pollen-only partner. Grow it to help fruiting vines nearby, not for its own crop.');
  }

  if (selected.fruitType === 'kiwi' && selected.pollinationRole !== 'male-pollinizer' && selected.id !== 'jenny') {
    notes.add('Most kiwi plantings need at least one male vine nearby to pollinate several fruiting females.');
  }

  if (selected.fruitType === 'hardy-kiwi' && selected.pollinationRole !== 'male-pollinizer' && selected.id !== 'issai') {
    notes.add('Most hardy kiwi plantings need at least one male vine nearby to pollinate several fruiting females.');
  }

  return [...notes];
}

function roleAwareSelfFertilityLabel(variety: FruitPollinationVariety): string {
  if (variety.pollinationRole === 'male-pollinizer') {
    return 'Grown as a pollen partner';
  }
  return SELF_FERTILITY_LABELS[variety.selfFertility];
}

function roleAwareSelfFertilitySentence(variety: FruitPollinationVariety): string {
  if (variety.pollinationRole === 'male-pollinizer') {
    return `${variety.name} is grown as a pollen partner rather than for fruit.`;
  }
  return SELF_FERTILITY_SENTENCES[variety.selfFertility](variety.name);
}

function crossPollinationReason(variety: FruitPollinationVariety): string {
  if (variety.pollinationRole === 'male-pollinizer') {
    return 'This plant is chosen to supply pollen for nearby fruiting vines or trees.';
  }

  switch (variety.fruitType) {
    case 'apple':
      return 'Apples almost always crop better with a different apple variety blooming at the same time.';
    case 'pear':
      return 'Pears often crop more reliably and carry heavier harvests when another pear blooms nearby.';
    case 'plum':
      return 'Plums are picky about pollination, so matching the right plum group is important.';
    case 'cherry':
      return variety.speciesGroup === 'tart'
        ? 'Tart cherries can fruit alone, but another tart cherry often improves yield.'
        : 'Most sweet cherries need a carefully matched sweet cherry partner.';
    case 'blueberry':
      return 'Even self-fertile blueberries usually make larger and better-filled berries with another compatible variety nearby.';
    case 'mango':
      return 'Most mangoes can fruit with their own pollen, but another mango nearby often improves fruit set, fruit size, and reliability when bloom weather is uneven.';
    case 'peach':
    case 'nectarine':
      return variety.selfFertility === 'not-self-fertile'
        ? 'This is one of the exceptions and should not be planted alone.'
        : 'Most peaches and nectarines can fruit alone, but a second compatible tree often improves fruit set and reliability.';
    case 'pawpaw':
      return 'Pawpaws usually need pollen from a different pawpaw cultivar nearby, and hand pollination can improve set.';
    case 'persimmon':
      return 'Many persimmons can fruit alone, but a second compatible persimmon often improves consistency and fruit fill.';
    case 'apricot':
      return variety.selfFertility === 'not-self-fertile'
        ? 'This apricot needs another apricot cultivar nearby.'
        : 'Many apricots can fruit alone, but another apricot often improves the crop.';
    case 'mulberry':
      return 'Named fruiting mulberries often fruit alone, but mixed plantings can improve harvest size and give you a longer season.';
    case 'elderberry':
      return 'Elderberries often crop better and more heavily when two cultivars bloom together.';
    case 'fig':
      return 'Common figs do not need a second tree for pollination, but a second variety can still give you more total fruit and better backup.';
    case 'grape':
      return 'Most grapes can fruit with their own pollen, but a second nearby grape with overlapping bloom can still improve yield and gives you backup if bloom weather is uneven.';
    case 'pomegranate':
      return 'Most pomegranates can fruit alone, but two plants often improve fruit set and crop consistency.';
    case 'passion-fruit':
      return variety.speciesGroup === 'incarnata'
        ? 'Maypop can set some fruit alone, but a separate compatible vine usually improves fruit set more reliably than another runner from the same patch.'
        : variety.selfFertility === 'self-fertile'
          ? 'This passion fruit can fruit alone, but another compatible vine often improves set and gives better backup through uneven bloom weather.'
          : 'Some passion fruit vines need or strongly benefit from a second compatible vine nearby for dependable fruit set.';
    case 'kiwi':
      return variety.id === 'jenny'
        ? 'Jenny can fruit on its own, but most kiwi plantings still perform better with another compatible kiwi nearby and other kiwi vines usually need a male pollinizer.'
        : 'Most kiwi vines need at least one compatible male pollinizer nearby, and one male can often support several fruiting females.';
    case 'serviceberry':
      return 'Serviceberries can fruit alone, but two cultivars usually give heavier crops and better berry fill.';
    case 'quince':
      return 'Quince is usually self-fruitful, but a second quince can still improve yield and consistency.';
    case 'hardy-kiwi':
      return variety.id === 'issai'
        ? 'Issai can fruit on its own, but most hardy kiwi plantings still perform better with another compatible kiwi nearby and other hardy kiwi vines usually need a male pollinizer.'
        : 'Most hardy kiwi vines need at least one compatible male pollinizer nearby, and one male can often support several fruiting females.';
    case 'gooseberry':
      return 'Gooseberries are usually self-fruitful, but another cultivar can still improve fruit size and total yield.';
    case 'currant':
      return 'Currants often fruit alone, but a second cultivar from the same currant group usually improves yield.';
    case 'jujube':
      return 'Most jujubes can fruit alone, but another cultivar often improves fruit set, size, and consistency.';
    case 'honeyberry':
      return 'Honeyberries require a second variety with overlapping bloom for any reliable fruit set. A lone plant will flower but rarely produce.';
    case 'hazelnut':
      return 'Hazelnuts are self-incompatible and wind-pollinated. Two S-allele-compatible varieties with overlapping catkin bloom are required for any crop - matching bloom timing alone is not enough if the S-alleles clash.';
    case 'raspberry':
      return variety.speciesGroup === 'black'
        ? 'Black raspberries are self-fertile but fruit noticeably better with a second black variety nearby. Red or yellow raspberries do not serve as effective cross-pollinizers for blacks.'
        : 'Red and yellow raspberries are self-fertile and fruit well alone, but a second variety of the same type can improve fruit set and extends the harvest window.';
    case 'blackberry':
      return 'Blackberries are self-fertile and produce a full crop without a companion, but a second variety with overlapping bloom extends the harvest season.';
    case 'strawberry':
      return 'Strawberries are self-fertile but require bee visitation for well-formed fruit. A second variety extends the harvest window and keeps pollinators active in the bed longer.';
    case 'aronia':
      return 'Aronia is self-fertile but consistently sets more abundant, larger fruit when a second variety blooms nearby. Any two varieties cross-pollinate effectively since they all bloom at the same time.';
    case 'goji-berry':
      return 'Goji berry is self-fertile but yields noticeably more fruit with two or more plants during the long summer bloom window.';
    case 'cranberry':
      return 'Cranberry sets less than 5% fruit from self-pollination alone, making a second genetically distinct variety functionally required for any meaningful harvest. Bumblebees are significantly more effective pollinators than honeybees.';
    case 'lingonberry':
      return 'Lingonberry is self-fertile but fruit set, berry size, and ripening speed all improve significantly with a cross-pollinator nearby. Red Pearl is the most documented and effective pollinizer.';
    default:
      return 'A compatible partner usually improves fruit set and crop consistency.';
  }
}

function matchDirection(selected: FruitPollinationVariety): PollinationPartnerMatch['direction'] {
  return selected.pollinationRole === 'male-pollinizer'
    ? 'supported-by-selected'
    : 'supports-selected';
}

function buildMatch(
  selected: FruitPollinationVariety,
  candidate: FruitPollinationVariety,
  zone: number | null,
): PollinationPartnerMatch {
  const direction = matchDirection(selected);
  const evaluation = evaluateCompatibility(selected, candidate, zone, direction);

  return {
    variety: candidate,
    rank: evaluation.rank,
    score: evaluation.score,
    bloomFit: evaluation.bloomFit,
    zoneFit: evaluation.zoneFit,
    chillFit: evaluation.chillFit,
    direction,
    reason: evaluation.reason,
    warnings: evaluation.warnings,
  };
}

function sortMatches(matches: PollinationPartnerMatch[]): PollinationPartnerMatch[] {
  const rankOrder: Record<PartnerRank, number> = { best: 0, good: 1, okay: 2, avoid: 3 };

  return [...matches].sort((a, b) => {
    if (rankOrder[a.rank] !== rankOrder[b.rank]) {
      return rankOrder[a.rank] - rankOrder[b.rank];
    }
    if (a.score !== b.score) return b.score - a.score;
    return a.variety.name.localeCompare(b.variety.name);
  });
}

function pairingRankFromScore(score: number, worksCount: number): StoreModePairing['rank'] {
  if (worksCount === 2 && score >= 165) return 'best';
  if (worksCount >= 1 && score >= 120) return 'good';
  if (worksCount >= 1 || score >= 90) return 'okay';
  return 'avoid';
}

function buildRankedPairings(
  fruitType: FruitPollinationTypeDefinition,
  varieties: FruitPollinationVariety[],
  zone: number | null,
): RankedPairing[] {
  const pairings: RankedPairing[] = [];

  for (let index = 0; index < varieties.length; index += 1) {
    for (let secondIndex = index + 1; secondIndex < varieties.length; secondIndex += 1) {
      const first = varieties[index];
      const second = varieties[secondIndex];
      const firstToSecond = evaluateCompatibility(first, second, zone, 'supports-selected');
      const secondToFirst = evaluateCompatibility(second, first, zone, 'supports-selected');
      const worksCount = Number(firstToSecond.donorToTarget.works) + Number(secondToFirst.donorToTarget.works);
      const roleBasedGoodPair = isRoleBasedGoodPair(first, second);
      const effectiveWorksCount = roleBasedGoodPair ? 2 : worksCount;
      const score = firstToSecond.score
        + secondToFirst.score
        + (effectiveWorksCount === 2 ? 24 : effectiveWorksCount === 1 ? 10 : 0)
        + (roleBasedGoodPair ? 24 : 0);
      const rank = pairingRankFromScore(score, effectiveWorksCount);
      const pairCheck = buildPairCheck(first, second);

      pairings.push({
        firstVariety: first,
        secondVariety: second,
        rank,
        score,
        summary: pairCheck.summary,
      });
    }
  }

  const rankOrder: Record<StoreModePairing['rank'], number> = { best: 0, good: 1, okay: 2, avoid: 3 };

  return pairings.sort((a, b) => {
    if (rankOrder[a.rank] !== rankOrder[b.rank]) return rankOrder[a.rank] - rankOrder[b.rank];
    if (a.score !== b.score) return b.score - a.score;
    return `${a.firstVariety.name}-${a.secondVariety.name}`.localeCompare(`${b.firstVariety.name}-${b.secondVariety.name}`);
  });
}

function buildCompareMissingCoverage(
  targetVarieties: FruitPollinationVariety[],
  supporterVarieties: FruitPollinationVariety[],
  zone: number | null,
): string[] {
  const missing: string[] = [];

  for (const target of targetVarieties) {
    const supporters = supporterVarieties.filter(candidate =>
      candidate.id !== target.id && evaluateCompatibility(target, candidate, zone).donorToTarget.works,
    );

    if (supporters.length === 0) {
      if (target.selfFertility === 'self-fertile') {
        missing.push(`${target.name} can still fruit alone, but another compatible variety nearby would usually improve fruit size, yield, and consistency.`);
      } else if (target.pollinationRole !== 'male-pollinizer') {
        missing.push(`${target.name} still needs a stronger partner in this list.`);
      }
    }
  }

  return missing;
}

function buildCompareVerdict(
  bestPairing: StoreModePairing | null,
  bestAdditions: PollinationAdditionSuggestion[],
  missingCoverage: string[],
): string {
  if (bestPairing && missingCoverage.length === 0) {
    return `This list already has a strong pollination pairing. ${bestPairing.firstVariety.name} and ${bestPairing.secondVariety.name} are the clearest reliable match from the varieties you entered.`;
  }

  if (bestPairing && bestAdditions[0]) {
    return `This list has one workable pairing, but it still leaves some gaps. ${bestPairing.firstVariety.name} and ${bestPairing.secondVariety.name} are your best starting point, and ${bestAdditions[0].variety.name} is the strongest add-on if you want better coverage.`;
  }

  if (bestAdditions[0]) {
    return `This list is still thin as entered. Add ${bestAdditions[0].variety.name} or swap in another better-matched variety from the same pollination group.`;
  }

  return 'This list does not yet form a strong planting plan. Choose a tighter bloom match from the same fruit group.';
}

function pairIncludesId(pair: StoreModePairing, varietyId: string): boolean {
  return pair.firstVariety.id === varietyId || pair.secondVariety.id === varietyId;
}

function isRoleBasedGoodPair(first: FruitPollinationVariety, second: FruitPollinationVariety): boolean {
  const isKiwiPair = (first.fruitType === 'kiwi' && second.fruitType === 'kiwi')
    || (first.fruitType === 'hardy-kiwi' && second.fruitType === 'hardy-kiwi');
  if (!isKiwiPair) return false;

  const firstIsMale = first.pollinationRole === 'male-pollinizer';
  const secondIsMale = second.pollinationRole === 'male-pollinizer';

  if (firstIsMale === secondIsMale) return false;

  return firstIsMale
    ? canDonorPollinate(second, first).works
    : canDonorPollinate(first, second).works;
}

function buildRankedPairingBetween(
  first: FruitPollinationVariety,
  second: FruitPollinationVariety,
  zone: number | null,
): RankedPairing {
  const firstToSecond = evaluateCompatibility(first, second, zone, 'supports-selected');
  const secondToFirst = evaluateCompatibility(second, first, zone, 'supports-selected');
  const worksCount = Number(firstToSecond.donorToTarget.works) + Number(secondToFirst.donorToTarget.works);
  const roleBasedGoodPair = isRoleBasedGoodPair(first, second);
  const effectiveWorksCount = roleBasedGoodPair ? 2 : worksCount;
  const score = firstToSecond.score
    + secondToFirst.score
    + (effectiveWorksCount === 2 ? 24 : effectiveWorksCount === 1 ? 10 : 0)
    + (roleBasedGoodPair ? 24 : 0);

  return {
    firstVariety: first,
    secondVariety: second,
    rank: pairingRankFromScore(score, effectiveWorksCount),
    score,
    summary: buildPairCheck(first, second).summary,
  };
}

function buildExistingVarietySummaries(
  existingVarieties: FruitPollinationVariety[],
  selectedVarieties: FruitPollinationVariety[],
  zone: number | null,
): ExistingVarietyPartnerSummary[] {
  return existingVarieties.map(existingVariety => {
    const pairings = selectedVarieties
      .filter(candidate => candidate.id !== existingVariety.id)
      .map(candidate => buildRankedPairingBetween(existingVariety, candidate, zone))
      .sort((a, b) => {
        const rankOrder: Record<StoreModePairing['rank'], number> = { best: 0, good: 1, okay: 2, avoid: 3 };
        if (rankOrder[a.rank] !== rankOrder[b.rank]) return rankOrder[a.rank] - rankOrder[b.rank];
        if (a.score !== b.score) return b.score - a.score;
        return a.secondVariety.name.localeCompare(b.secondVariety.name);
      });

    const bestPartners = pairings.filter(pair => pair.rank === 'best' || pair.rank === 'good').slice(0, 4);
    const weakPartners = pairings.filter(pair => pair.rank === 'okay' || pair.rank === 'avoid').slice(0, 4);
    const missingMessage = bestPartners.length > 0
      ? null
      : existingVariety.selfFertility === 'self-fertile'
        ? `${existingVariety.name} can still fruit alone, but none of the other varieties in this list look like especially strong upgrades.`
        : `None of the other varieties in this list look like a strong partner for ${existingVariety.name}.`;

    return {
      existingVariety,
      bestPartners,
      weakPartners,
      missingMessage,
    };
  });
}

function buildExistingFocusedVerdict(
  existingVarieties: FruitPollinationVariety[],
  candidateVarieties: FruitPollinationVariety[],
  bestPairing: StoreModePairing | null,
  missingCoverage: string[],
): string {
  if (candidateVarieties.length === 0) {
    if (missingCoverage.length === 0) {
      return `You marked ${existingVarieties.map(variety => variety.name).join(', ')} as already owned, and that existing set already has workable pollination coverage.`;
    }

    return `You marked ${existingVarieties.map(variety => variety.name).join(', ')} as already owned, but there are no candidate additions in this list yet.`;
  }

  if (bestPairing && missingCoverage.length === 0) {
    return `These recommendations are focused on what you already have. ${bestPairing.firstVariety.name} and ${bestPairing.secondVariety.name} are the strongest match between your existing trees and the varieties you are considering.`;
  }

  if (bestPairing) {
    return `These recommendations are focused on what you already have. ${bestPairing.firstVariety.name} and ${bestPairing.secondVariety.name} are the clearest starting point from this list, but you still have some gaps to watch.`;
  }

  return 'These recommendations are focused on what you already have, and this list still looks weak for those existing trees.';
}

function buildExistingSimpleRecommendation(
  existingVarieties: FruitPollinationVariety[],
  existingVarietySummaries: ExistingVarietyPartnerSummary[],
  candidateVarieties: FruitPollinationVariety[],
): string {
  if (candidateVarieties.length === 0) {
    return existingVarieties.length > 1
      ? `You already marked ${existingVarieties.map(variety => variety.name).join(' and ')} as owned. Add candidate varieties to see which new tree best supports that existing planting.`
      : `You already marked ${existingVarieties[0].name} as owned. Add candidate varieties to see which new tree best supports it.`;
  }

  if (existingVarieties.length === 1) {
    const summary = existingVarietySummaries[0];
    const partnerNames = summary?.bestPartners.slice(0, 3).map(pair =>
      pair.firstVariety.id === summary.existingVariety.id ? pair.secondVariety.name : pair.firstVariety.name,
    ) ?? [];

    if (partnerNames.length > 0) {
      return `Since you already have ${existingVarieties[0].name}, choose ${partnerNames.join(' or ')} from this list.`;
    }

    return `Since you already have ${existingVarieties[0].name}, none of the unmarked varieties here look like especially strong partners.`;
  }

  const supportedExisting = existingVarietySummaries.filter(summary => summary.bestPartners.length > 0);
  if (supportedExisting.length === existingVarietySummaries.length) {
    const candidateNames = Array.from(new Set(
      supportedExisting.flatMap(summary =>
        summary.bestPartners.slice(0, 2).map(pair =>
          pair.firstVariety.id === summary.existingVariety.id ? pair.secondVariety.name : pair.firstVariety.name,
        ),
      ),
    )).slice(0, 4);

    if (candidateNames.length > 0) {
      return `Because you already have ${existingVarieties.map(variety => variety.name).join(' and ')}, prioritize ${candidateNames.join(', ')} from this list to strengthen the planting you already own.`;
    }
  }

  return `You already have ${existingVarieties.map(variety => variety.name).join(' and ')}. Focus on the unmarked varieties that directly support those existing trees first.`;
}

function buildPairCheck(selected: FruitPollinationVariety, secondVariety: FruitPollinationVariety): PollinationPairCheck {
  const firstToSecond = evaluateCompatibility(secondVariety, selected, null, 'supports-selected');
  const secondToFirst = evaluateCompatibility(selected, secondVariety, null, 'supports-selected');
  const roleBasedGoodPair = isRoleBasedGoodPair(selected, secondVariety);
  const details: string[] = [];

  if (roleBasedGoodPair) {
    const male = selected.pollinationRole === 'male-pollinizer' ? selected : secondVariety;
    const fruiting = selected.pollinationRole === 'male-pollinizer' ? secondVariety : selected;
    details.push(`${male.name} should be able to supply pollen for ${fruiting.name}.`);
    details.push(`${fruiting.name} is the fruiting vine in this pairing, so it does not need to return pollen to the male plant.`);
    const kiwiLabel = selected.fruitType === 'kiwi' ? 'kiwi' : 'hardy kiwi';
    return {
      status: 'good',
      summary: `This is the right kind of ${kiwiLabel} pairing: one compatible male pollinizer supporting one fruiting vine.`,
      details,
    };
  }

  if (secondToFirst.donorToTarget.works) {
    details.push(`${secondVariety.name} should be able to help ${selectedVarietyLabel(selected)}.`);
  } else {
    details.push(`${secondVariety.name} is not a dependable partner for ${selectedVarietyLabel(selected)}.`);
  }

  if (firstToSecond.donorToTarget.works) {
    details.push(`${selected.name} should also be able to help ${selectedVarietyLabel(secondVariety)}.`);
  } else if (selected.pollenSterile || selected.pollinationRole === 'male-pollinizer') {
    details.push(`${selected.name} is not a normal fruiting partner in the other direction.`);
  } else {
    details.push(`${selected.name} is not a dependable partner for ${selectedVarietyLabel(secondVariety)}.`);
  }

  if (secondToFirst.donorToTarget.works && firstToSecond.donorToTarget.works) {
    return { status: 'good', summary: 'These two should make a strong planting pair.', details };
  }

  if (secondToFirst.donorToTarget.works || firstToSecond.donorToTarget.works) {
    return { status: 'limited', summary: 'This pairing may help one plant more than the other.', details };
  }

  return { status: 'not-compatible', summary: 'These two are not a dependable match.', details };
}

function partnerBuckets(matches: PollinationPartnerMatch[]) {
  const sorted = sortMatches(matches);
  return {
    bestMatches: sorted.filter(match => match.rank === 'best'),
    goodBackups: sorted.filter(match => match.rank === 'good'),
    lessIdealMatches: sorted.filter(match => match.rank === 'okay'),
    avoidMatches: sorted.filter(match => match.rank === 'avoid'),
  };
}

function buildAdditionSuggestions(
  fruitType: FruitPollinationTypeDefinition,
  selectedVarieties: FruitPollinationVariety[],
  zone: number | null,
): PollinationAdditionSuggestion[] {
  const selectedIds = new Set(selectedVarieties.map(variety => variety.id));
  const additions: Array<PollinationAdditionSuggestion & { score: number }> = fruitType.varieties
    .filter(candidate => !selectedIds.has(candidate.id))
    .map(candidate => {
      const improvesVarieties = selectedVarieties.filter(variety => evaluateCompatibility(variety, candidate, zone).donorToTarget.works);
      const score = improvesVarieties.reduce((sum, variety) => sum + evaluateCompatibility(variety, candidate, zone).score, 0);
      return {
        variety: candidate,
        rank: improvesVarieties.length >= 2
          ? 'best'
          : improvesVarieties.length === 1
            ? 'good'
            : 'okay' as PollinationAdditionSuggestion['rank'],
        coverageCount: improvesVarieties.length,
        improvesVarieties,
        reason: improvesVarieties.length > 0
          ? `Helps ${improvesVarieties.map(variety => variety.name).join(', ')} and improves overall coverage.`
          : 'Does not solve a clear gap for the current selection.',
        score,
      };
    })
    .filter(item => item.coverageCount > 0)
    .sort((a, b) => {
      if (a.coverageCount !== b.coverageCount) return b.coverageCount - a.coverageCount;
      return b.score - a.score;
    })
    .slice(0, 8);

  return additions.map(item => ({
    variety: item.variety,
    rank: item.rank,
    coverageCount: item.coverageCount,
    improvesVarieties: item.improvesVarieties,
    reason: item.reason,
  }));
}

function collectImportantWarnings(varieties: FruitPollinationVariety[]): string[] {
  const warnings = new Set<string>();
  for (const variety of varieties) {
    for (const warning of sameTypeWarning(variety, null)) warnings.add(warning);
  }
  return [...warnings];
}

export function listFruitPollinationTypes(): FruitPollinationTypeDefinition[] {
  return Object.values(FRUIT_POLLINATION_DATA)
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function getFruitPollinationType(type: FruitPollinationFruitType): FruitPollinationTypeDefinition {
  return FRUIT_POLLINATION_DATA[type];
}

export function getFruitPollinationVariety(
  fruitType: FruitPollinationFruitType,
  varietyId: string,
): FruitPollinationVariety | null {
  return FRUIT_POLLINATION_DATA[fruitType].varieties.find(variety => variety.id === varietyId) ?? null;
}

export function resolveFruitPollinationVarietyByQuery(
  fruitType: FruitPollinationFruitType,
  query: string,
): FruitPollinationVariety | null {
  const normalizedQuery = normalizeComparableText(query);
  if (!normalizedQuery) return null;

  const fruit = getFruitPollinationType(fruitType);
  let partialMatch: FruitPollinationVariety | null = null;

  for (const variety of fruit.varieties) {
    const candidates = varietyLookupCandidates(variety);
    if (candidates.includes(normalizedQuery)) return variety;
    if (!partialMatch && candidates.some(candidate => candidate.includes(normalizedQuery) || normalizedQuery.includes(candidate))) {
      partialMatch = variety;
    }
  }

  return partialMatch;
}

export function buildFruitPollinationResult(
  fruitType: FruitPollinationFruitType,
  varietyId: string,
  zone: number | null = null,
  secondVarietyId?: string | null,
): FruitPollinationResult | null {
  const fruit = getFruitPollinationType(fruitType);
  const selectedVariety = getFruitPollinationVariety(fruitType, varietyId);
  if (!selectedVariety) return null;

  const secondVariety = secondVarietyId
    ? getFruitPollinationVariety(fruitType, secondVarietyId)
    : null;

  const matches = fruit.varieties
    .filter(candidate => candidate.id !== selectedVariety.id)
    .map(candidate => buildMatch(selectedVariety, candidate, zone));

  const buckets = partnerBuckets(matches);

  return {
    fruitType: fruit,
    selectedVariety,
    secondVariety,
    zone,
    selfFertilityLabel: roleAwareSelfFertilityLabel(selectedVariety),
    selfFertilitySentence: roleAwareSelfFertilitySentence(selectedVariety),
    crossPollinationRecommended: true,
    crossPollinationReason: crossPollinationReason(selectedVariety),
    bloomSummary: bloomSummary(selectedVariety),
    zoneSummary: zoneSummary(selectedVariety, zone),
    importantWarnings: sameTypeWarning(selectedVariety, secondVariety),
    bestMatches: buckets.bestMatches,
    goodBackups: buckets.goodBackups,
    lessIdealMatches: buckets.lessIdealMatches,
    avoidMatches: buckets.avoidMatches,
    pairCheck: secondVariety ? buildPairCheck(selectedVariety, secondVariety) : null,
    plainLanguageNotes: buildNotes(selectedVariety, fruit),
    plantingGuidance: plantingGuidance(fruit),
  };
}

export function buildCompareVarietiesResult(
  fruitType: FruitPollinationFruitType,
  varietyIds: string[],
  zone: number | null = null,
  existingVarietyIds: string[] = [],
): CompareVarietiesResult | null {
  const fruit = getFruitPollinationType(fruitType);
  const selectedVarieties = varietyIds
    .map(varietyId => getFruitPollinationVariety(fruitType, varietyId))
    .filter((variety): variety is FruitPollinationVariety => Boolean(variety));

  if (selectedVarieties.length < 2) return null;

  const existingIdSet = new Set(existingVarietyIds);
  const existingVarieties = selectedVarieties.filter(variety => existingIdSet.has(variety.id));
  const candidateVarieties = selectedVarieties.filter(variety => !existingIdSet.has(variety.id));
  const usesExistingVarietyFocus = existingVarieties.length > 0;
  const pairings = buildRankedPairings(fruit, selectedVarieties, zone);
  const existingVarietySummaries = usesExistingVarietyFocus
    ? buildExistingVarietySummaries(existingVarieties, selectedVarieties, zone)
    : [];

  const targetedPairings = usesExistingVarietyFocus
    ? pairings.filter(pair => existingVarieties.some(variety => pairIncludesId(pair, variety.id)))
    : pairings;

  const targetedGood = targetedPairings.filter(pair => pair.rank === 'best' || pair.rank === 'good');
  const bestPairing = targetedGood[0] ?? targetedPairings.find(pair => pair.rank === 'okay') ?? null;
  const missingCoverage = usesExistingVarietyFocus
    ? buildCompareMissingCoverage(existingVarieties, selectedVarieties, zone)
    : buildCompareMissingCoverage(selectedVarieties, selectedVarieties, zone);
  const bestAdditions = usesExistingVarietyFocus
    ? []
    : buildAdditionSuggestions(fruit, selectedVarieties, zone);
  const otherWorkablePairings = targetedGood.slice(bestPairing ? 1 : 0, 5);
  const generalAlternativePairings = usesExistingVarietyFocus
    ? pairings
      .filter(pair =>
        !existingVarieties.some(variety => pairIncludesId(pair, variety.id))
        && (pair.rank === 'best' || pair.rank === 'good')
      )
      .slice(0, 4)
    : [];
  const weakCombinations = usesExistingVarietyFocus
    ? targetedPairings.filter(pair => pair.rank === 'okay' || pair.rank === 'avoid').slice(0, 6)
    : pairings.filter(pair => pair.rank === 'okay' || pair.rank === 'avoid').slice(0, 6);
  const verdict = usesExistingVarietyFocus
    ? buildExistingFocusedVerdict(existingVarieties, candidateVarieties, bestPairing, missingCoverage)
    : buildCompareVerdict(bestPairing, bestAdditions, missingCoverage);
  const simpleRecommendation = usesExistingVarietyFocus
    ? buildExistingSimpleRecommendation(existingVarieties, existingVarietySummaries, candidateVarieties)
    : verdict;

  return {
    fruitType: fruit,
    selectedVarieties,
    existingVarieties,
    candidateVarieties,
    usesExistingVarietyFocus,
    zone,
    verdict,
    bestPairing,
    otherWorkablePairings,
    existingVarietySummaries,
    generalAlternativePairings,
    weakCombinations,
    missingCoverage,
    simpleRecommendation,
    bestAdditions,
    importantWarnings: collectImportantWarnings(selectedVarieties),
    plantingGuidance: plantingGuidance(fruit),
    frostRiskNote: buildFrostRiskNote(fruitType, selectedVarieties),
  };
}

export function cleanClaudeOutput(text: string): string {
  const lines = text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\r\n/g, '\n')
    .split('\n');

  const cleaned = lines
    .map(line => line.trim())
    .filter(line => !/^(-{3,}|\*{3,}|_{3,})$/.test(line))
    .map(line => line.replace(/^#{1,6}\s*/, ''))
    .map(line => line.replace(/^>\s*/, ''))
    .map(line => line.replace(/^[-*+•]\s+/, ''))
    .map(line => line.replace(/^\d+\.\s+/, ''))
    .map(line => line.replace(/[*_`]/g, ''))
    .map(line => line.replace(/\s+/g, ' ').trim());

  const paragraphs: string[] = [];
  let buffer: string[] = [];

  for (const line of cleaned) {
    if (!line) {
      if (buffer.length > 0) {
        paragraphs.push(buffer.join(' '));
        buffer = [];
      }
      continue;
    }

    buffer.push(line);
  }

  if (buffer.length > 0) paragraphs.push(buffer.join(' '));

  return paragraphs.join('\n\n').trim();
}

function joinNames(names: string[], conjunction = 'and'): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} ${conjunction} ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, ${conjunction} ${names[names.length - 1]}`;
}

/** "Bloom group 4 - mid bloom." reads better as "bloom group 4, which is mid bloom". */
function bloomPhrase(result: FruitPollinationResult): string {
  const summary = result.bloomSummary.replace(/\.$/, '');
  const parsed = summary.match(/^Bloom group (.+?) - (.+)$/);
  if (!parsed) return summary.toLowerCase();
  const [, group, window] = parsed;
  if (window.toLowerCase().includes(group.toLowerCase())) return window;
  return `bloom group ${group}, which is ${window}`;
}

function explanationOpening(result: FruitPollinationResult): string {
  const parts = [result.selfFertilitySentence, result.crossPollinationReason];

  if (result.selectedVariety.pollinationRole !== 'male-pollinizer') {
    const bloom = bloomPhrase(result);
    parts.push(result.selectedVariety.selfFertility === 'self-fertile'
      ? `It flowers in ${bloom}, which is worth knowing if you add a second tree later.`
      : `It flowers in ${bloom}, so a partner has to be flowering at the same time to be any use.`);
  }

  if (result.zoneSummary) parts.push(result.zoneSummary);
  return parts.join(' ');
}

function explanationPartners(result: FruitPollinationResult): string {
  const names = (matches: PollinationPartnerMatch[]) => matches.slice(0, 5).map(match => match.variety.name);
  const best = names(result.bestMatches);
  const good = names(result.goodBackups);
  const spacing = result.plainLanguageNotes[0] ?? '';

  if (best.length > 0) {
    const extra = result.bestMatches.length - best.length;
    const more = extra > 0 ? ` There are ${extra} more in the full list below.` : '';
    const backup = good.length > 0
      ? ` If your nursery has none of those, ${joinNames(good.slice(0, 3), 'or')} will also do the job.`
      : '';
    return `The good news is you have real choices here. The strongest partners are ${joinNames(best)}.${more} Any one of them is enough to get fruit set going.${backup} ${spacing}`;
  }

  if (good.length > 0) {
    const extra = result.goodBackups.length - good.length;
    const more = extra > 0 ? ` There are ${extra} more in the list below.` : '';
    return `No single variety stands out as the obvious partner, but that is not a problem here: ${joinNames(good)} all bloom close enough to work.${more} Pick whichever your nursery actually carries. ${spacing}`;
  }

  const lessIdeal = names(result.lessIdealMatches).slice(0, 3);
  if (lessIdeal.length > 0) {
    return `This is a harder one to partner. Nothing in the group is a strong match, and ${joinNames(lessIdeal)} are the closest options, so expect a lighter crop than a well-matched pair would give. ${spacing}`;
  }

  return `Happily, this crop does not depend on a partner variety the way tree fruit does, so there is no ranked list to choose from. ${spacing}`;
}

function explanationPairCheck(result: FruitPollinationResult): string {
  if (!result.pairCheck || !result.secondVariety) return '';
  const verdict = result.pairCheck.status === 'good'
    ? 'that is a good pairing'
    : result.pairCheck.status === 'limited'
      ? 'that pairing is workable but limited'
      : 'that is not a pairing to rely on';
  return `You also checked ${result.selectedVariety.name} against ${result.secondVariety.name}, and ${verdict}. ${result.pairCheck.summary}`;
}

function explanationCautions(result: FruitPollinationResult): string {
  const cautions = [...result.importantWarnings, ...result.plainLanguageNotes.slice(1)];
  if (cautions.length === 0) return '';
  return `One thing to keep in mind. ${cautions.join(' ')}`;
}

function explanationPlanting(result: FruitPollinationResult): string {
  const { minimumSetup, idealSetup, fallbackPlan } = result.plantingGuidance;
  const lower = (text: string) => `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
  return `To put that into practice: at a minimum, ${lower(minimumSetup)} Better still, ${lower(idealSetup)} ${fallbackPlan}`;
}

/**
 * Written locally rather than by a model. Everything the explanation needs is
 * already structured on the result, so generating it here keeps the answer
 * instant, free, zone-aware, and available to a crawler on first paint.
 */
export function buildFruitPollinationFallbackExplanation(result: FruitPollinationResult): string {
  return [
    explanationOpening(result),
    explanationPartners(result),
    explanationPairCheck(result),
    explanationCautions(result),
    explanationPlanting(result),
  ]
    .map(paragraph => paragraph.trim().replace(/[ \t]{2,}/g, ' ').replace(/ +([.,])/g, '$1'))
    .filter(Boolean)
    .join('\n\n');
}

export function buildCompareVarietiesFallbackExplanation(result: CompareVarietiesResult): string {
  const intro = result.usesExistingVarietyFocus
    ? `These results were focused on the varieties you already have: ${result.existingVarieties.map(variety => variety.name).join(', ')}. Candidate additions checked were ${result.candidateVarieties.map(variety => variety.name).join(', ') || 'none yet'}.`
    : `${result.selectedVarieties.map(variety => variety.name).join(', ')} were checked together for ${result.fruitType.label.toLowerCase()} pollination.`;
  const verdict = result.verdict;
  const best = result.bestPairing
    ? result.usesExistingVarietyFocus
      ? `The strongest partner choice from this list is ${result.bestPairing.firstVariety.name} with ${result.bestPairing.secondVariety.name}.`
      : `The strongest pairing in this list is ${result.bestPairing.firstVariety.name} with ${result.bestPairing.secondVariety.name}.`
    : result.usesExistingVarietyFocus
      ? 'This list does not yet contain a clearly strong partner for the trees you already have.'
      : 'This list does not yet contain a clearly strong pairing.';
  const focus = result.usesExistingVarietyFocus && result.existingVarietySummaries.length > 0
    ? result.existingVarietySummaries
      .map(summary => {
        const names = summary.bestPartners.slice(0, 3).map(pair =>
          pair.firstVariety.id === summary.existingVariety.id ? pair.secondVariety.name : pair.firstVariety.name,
        );
        if (names.length > 0) {
          return `For ${summary.existingVariety.name}, the strongest partners in this list are ${names.join(', ')}.`;
        }

        return summary.missingMessage ?? `No especially strong partners stand out for ${summary.existingVariety.name}.`;
      })
      .join(' ')
    : '';
  const backups = result.otherWorkablePairings.length > 0
    ? `Other workable pairings include ${result.otherWorkablePairings.slice(0, 3).map(pair => `${pair.firstVariety.name} with ${pair.secondVariety.name}`).join(', ')}.`
    : '';
  const recommendation = result.simpleRecommendation;
  const missing = result.missingCoverage.length > 0
    ? result.missingCoverage.join(' ')
    : 'No major pollination gaps stand out in this group.';
  const additions = !result.usesExistingVarietyFocus && result.bestAdditions.length > 0
    ? `If you want to improve the list, the strongest additions are ${result.bestAdditions.slice(0, 3).map(item => item.variety.name).join(', ')}.`
    : '';
  const alternatives = result.generalAlternativePairings.length > 0
    ? `General alternatives that do not rely on your existing trees include ${result.generalAlternativePairings.slice(0, 3).map(pair => `${pair.firstVariety.name} with ${pair.secondVariety.name}`).join(', ')}.`
    : '';
  return cleanClaudeOutput([intro, verdict, best, focus, backups, recommendation, missing, additions, alternatives].filter(Boolean).join('\n\n'));
}
